import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, Loader2, ArrowLeft, ShieldCheck, CheckCircle } from 'lucide-react';
import AuthLayout from '../components/auth/AuthLayout';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    // Fake network call
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
    }, 2000);
  };

  return (
    <AuthLayout>

          <div className="mb-6 text-center lg:text-left">
            {!success && (
              <Link to="/login" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-brand-black transition-colors mb-6">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Link>
            )}

            {success ? (
              <div className="mb-8">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6 mx-auto lg:mx-0">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-black mb-3">Password Reset</h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Your password has been successfully updated. You can now use your new credentials to sign in.
                </p>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-black mb-3">Set New Password</h2>
                <p className="text-gray-500 text-sm">Create a strong password that you don't use elsewhere.</p>
              </div>
            )}
          </div>

          {success ? (
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-brand-orange-500 text-white py-3 rounded-xl font-bold hover:bg-brand-orange-600 transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              Sign In with New Password
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-brand-black uppercase tracking-wide mb-2">New Password</label>
                <div className="relative">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                    disabled={loading}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-brand-black uppercase tracking-wide mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                    disabled={loading}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300">
                    <Eye className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Must match your new password above.</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-orange-500 text-white py-3 rounded-xl font-bold hover:bg-brand-orange-600 transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {loading ? 'Reseting Password...' : 'Reset Password'}
              </button>
            </form>
          )}
    </AuthLayout>
  );
};

export default ResetPassword;
